import React, { useEffect, useState } from "react";
import { Stack, Text, TextField, PrimaryButton, Dropdown, IDropdownOption, Spinner, SpinnerSize } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { fetchAllDetails, getTokenAndListInfo } from "../../utils/item-helper";
import { useMsal } from "@azure/msal-react";
import { getDropdownOptionsFromList, getNamesByIds } from "../../services/graph-service";
import { listname } from "../../constants/global-values";
import { useMediaQuery } from 'react-responsive';
import '../../assets/styles/ky-activity.css';
import { kyActivityPage } from "../../messages/ky-activity";
import { common } from "../../messages/common-items";
import { RISK_MEASURE_MAP, RISKY_WORK_OPTIONS } from "../../constants/options";

const KYActivityPage: React.FC = () => {
  const today = new Date().toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any>(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { Labels: Lbl_KyActivity, ErrorMessages: Err_KyActivity } = kyActivityPage;
  const { Labels: Lbl_Common, SuccessMessages: Success_Common } = common;
  const [formData, setFormData] = useState({
    ClientID: "",
    OnSiteID: "",
    KYDate: null,
    LeaderName: "",
    PARTNER_NAME_LBL: "",
    FOREMAN_NAME: "",
    WORK_DETAILS: "",
    RISKY_WORK: [] as string[],
    RISK_AVOIDANCE: [] as string[],
  });
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [selectedRisks, setSelectedRisks] = useState<Record<string, string[]>>({});
  const handleTaskChange = (_: any, option?: IDropdownOption) => {
    if (!option) return;
    const key = option.key as string;
    setSelectedTasks((prev) => {
      const exists = prev.includes(key);
      const updated = exists ? prev.filter((k) => k !== key) : [...prev, key];
      if (exists) {
        const updatedRisks = { ...selectedRisks };
        delete updatedRisks[key];
        setSelectedRisks(updatedRisks);
      }
      return updated;
    });
  };

  const handleRiskChange = (taskKey: string, option?: IDropdownOption) => {
    if (!option) return;
    setSelectedRisks((prev) => {
      const current = prev[taskKey] || [];
      const riskKey = option.key as string;
      const updated = current.includes(riskKey)
        ? current.filter((k) => k !== riskKey)
        : [...current, riskKey];

      return { ...prev, [taskKey]: updated };
    });
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const renderRow = (label: string, field: React.ReactNode) => (
    <Stack horizontal className="form-row" verticalAlign="center">
      <Text className="form-label">{label}:</Text>
      <div className="form-field">{field}</div>
    </Stack>
  );

  const [foremanOptions, setForemanOptions] = useState<IDropdownOption[]>([]);
  const [participantNames, setParticipantNames] = useState<string>("");
  const participantIds = "9,17,18,19,21";
  useEffect(() => {
    async function loadDetails() {
      setLoading(true);
      try {
        const result = await fetchAllDetails(instance, {
          contractId: "1",
          siteId: "1",
          personalId: "1",
          partnerId: "1",
        });
        setDetails(result);
        const { token, siteId, listId } = await getTokenAndListInfo(instance, listname);
        const foremen = await getDropdownOptionsFromList(token, siteId, listId, "Name", "ID");
        setForemanOptions(foremen);
        const idsArray = participantIds.split(",").map(id => id.trim());
        const names = await getNamesByIds(token, siteId, listId, idsArray, "Name");
        setParticipantNames(names.join(", "));
      }
      catch (error) {
        console.error("Error loading item", error);
      } finally {
        setLoading(false);
      }
    }
    loadDetails();
  }, [instance]);
  if (loading || !details) return <div className="loadercommon-container">
    <Spinner label={Success_Common.LOADER_MSG} size={SpinnerSize.large} />
  </div>;

  return (
    <div className="background list-background">
      <div className={`form-container ${isMobile ? "form-box" : "list-box"}`} >
        <div className="content-container">
          <div className={`${isMobile ? "title-mobile" : "header-row"}`}>
            <h2 className="compactHeading">{Lbl_KyActivity.KYACTIVITY_PAGE}</h2>
          </div>
          <Stack tokens={{ childrenGap: 10 }}>
            <Stack style={{ marginBottom: 15 }}>
              {isMobile
                ? (
                  <>
                    <TextField label={Lbl_KyActivity.SITE_NAME} value={"戸田建設株式会社"} readOnly className="nonInteractiveTextField" />
                    <TextField label={Lbl_KyActivity.CLIENT_NAME} value={details.contractName} readOnly className="nonInteractiveTextField" />
                    <TextField label={Lbl_KyActivity.SITE_NAME} value={details.siteName} readOnly className="nonInteractiveTextField" />
                    <TextField label={Lbl_KyActivity.KY_DATE} value={today} readOnly className="nonInteractiveTextField" />
                    <TextField label={Lbl_KyActivity.LEADER_NAME} value={details.leaderName} readOnly className="nonInteractiveTextField" />
                    <TextField label={Lbl_KyActivity.PARTNER_NAME} value={details.partnerCompany} readOnly className="nonInteractiveTextField" />
                    <Dropdown
                      label={Lbl_KyActivity.FOREMAN_NAME}
                      placeholder={Err_KyActivity.FOREMAN_REQ_ERR}
                      id="FOREMAN_NAME"
                      options={foremanOptions}
                      onChange={(_, option) => updateField("FOREMAN_NAME", option?.text)}
                    />
                    <TextField label={Lbl_KyActivity.PARTICIPANTS} value={participantNames} readOnly className="nonInteractiveTextField" />
                    <TextField
                      label={Lbl_KyActivity.WORK_DETAILS}
                      placeholder={Err_KyActivity.WORKDETAILS_REQ_ERR}
                      multiline rows={6}
                      value={formData.WORK_DETAILS}
                      onChange={(_, val) => updateField("WORK_DETAILS", val)}
                    />
                    <TextField label={Lbl_KyActivity.SAFETY_INSTRUCTIONS}
                      value={"第三者優先、立ち入り禁止区域への進入禁止、適切な人員配置、作業区画の明示、段差･開口部の確認、脚立適正使用、保護具着用"}
                      readOnly className="nonInteractiveTextField" />
                    <Stack tokens={{ childrenGap: 10 }}>
                      <Dropdown
                        label={Lbl_KyActivity.RISKY_WORK}
                        placeholder={Err_KyActivity.RISKYWORK_REQ_ERR}
                        multiSelect
                        options={RISKY_WORK_OPTIONS}
                        selectedKeys={selectedTasks}
                        onChange={handleTaskChange}
                        className="dropdownGroup"
                      />
                      {selectedTasks.map((taskKey) => (
                        <Dropdown
                          key={taskKey}
                          label={`${Lbl_KyActivity.RISK_AVOIDANCE} "${RISKY_WORK_OPTIONS.find(o => o.key === taskKey)?.text}"`}
                          multiSelect
                          options={RISK_MEASURE_MAP[taskKey] || []}
                          selectedKeys={selectedRisks[taskKey] || []}
                          onChange={(_, option) => handleRiskChange(taskKey, option)}
                          className="dropdownGroup"
                        />
                      ))}
                    </Stack>
                  </>
                ) : (
                  <>
                    {renderRow(Lbl_KyActivity.SITE_NAME, <Text>戸田建設株式会社</Text>)}
                    {renderRow(Lbl_KyActivity.CLIENT_NAME, <Text>{details.contractName}</Text>)}
                    {renderRow(Lbl_KyActivity.SITE_NAME, <Text>{details.siteName}</Text>)}
                    {renderRow(Lbl_KyActivity.KY_DATE, <Text>{today}（{Lbl_KyActivity.TODAYS_DATE}）</Text>)}
                    {renderRow(Lbl_KyActivity.LEADER_NAME, <Text>{details.leaderName}</Text>)}
                    {renderRow(Lbl_KyActivity.PARTNER_NAME, <Text>{details.partnerCompany}</Text>)}
                    {renderRow(
                      Lbl_KyActivity.FOREMAN_NAME,
                      <Dropdown id="FOREMAN_NAME" options={foremanOptions} placeholder={Err_KyActivity.FOREMAN_REQ_ERR}
                        onChange={(_, option) => updateField("FOREMAN_NAME", option?.text)} />
                    )}
                    {renderRow(
                      Lbl_KyActivity.PARTICIPANTS,
                      <Text>{participantNames}</Text>
                    )}
                    {renderRow(
                      Lbl_KyActivity.WORK_DETAILS,
                      <TextField
                        placeholder={Err_KyActivity.WORKDETAILS_REQ_ERR}
                        multiline
                        rows={6}
                        value={formData.WORK_DETAILS}
                        onChange={(_, val) => updateField("WORK_DETAILS", val)}
                      />
                    )}
                    {renderRow(
                      Lbl_KyActivity.SAFETY_INSTRUCTIONS,
                      <Text>第三者優先、立ち入り禁止区域への進入禁止、適切な人員配置、作業区画の明示、段差･開口部の確認、脚立適正使用、保護具着用</Text>
                    )}
                    {renderRow(
                      Lbl_KyActivity.RISKY_WORK,
                      <Dropdown
                        placeholder={Err_KyActivity.RISKYWORK_REQ_ERR}
                        multiSelect
                        options={RISKY_WORK_OPTIONS}
                        selectedKeys={selectedTasks}
                        onChange={handleTaskChange}
                        styles={{ dropdown: { width: 350 } }}
                      />
                    )}
                    <Stack tokens={{ childrenGap: 20 }}>
                      {selectedTasks.map((taskKey) => (
                        <div style={{ marginBottom: "10px", marginLeft: "122px" }} key={taskKey}>
                          <Dropdown
                            key={taskKey}
                            label={`${Lbl_KyActivity.RISK_AVOIDANCE} "${RISKY_WORK_OPTIONS.find(o => o.key === taskKey)?.text}"`}
                            multiSelect
                            options={RISK_MEASURE_MAP[taskKey] || []}
                            selectedKeys={selectedRisks[taskKey] || []}
                            onChange={(_, option) => handleRiskChange(taskKey, option)}
                            styles={{ dropdown: { width: 350 } }}
                          />
                        </div>
                      ))}
                    </Stack>
                  </>
                )
              }
            </Stack>
            <Stack horizontal className="buttonallign" tokens={{ childrenGap: 14 }} >
              <PrimaryButton className="custom-primary-button" text={Lbl_Common.COMPLETION} onClick={() => alert(Success_Common.SENT_MSG)} />
              <PrimaryButton className="cancel-button" text={Lbl_Common.CANCEL} onClick={() => navigate(-1)} />
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
};

export default KYActivityPage;